import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  OTHER_CODE,
  OTHER_TEXT_MAX,
  COPY,
} from "@/data/waitlistOptions";
import {
  validateWaitlist,
  FIELD_FOCUS_ORDER,
} from "@/lib/waitlistValidation";
import { submitWaitlist } from "@/lib/waitlistSubmission";
import { fetchWaitlistOptions } from "@/lib/waitlistApi";
import { captureAttribution } from "@/lib/waitlistAttribution";
import { MultiSelectOptionGroup } from "./MultiSelectOptionGroup";
import { LANDING } from "@/constants/testIds/waitlist";

const FieldError = ({ id, message }) =>
  message ? (
    <p id={id} data-testid={id} className="mt-1.5 text-sm text-red-400">
      {message}
    </p>
  ) : null;

const inputBase =
  "w-full rounded-sm bg-white/[0.03] border border-white/20 px-4 py-3 text-white placeholder-white/30 " +
  "focus:outline-none focus:border-[#C8A24C] focus:ring-1 focus:ring-[#C8A24C] transition-colors " +
  "disabled:opacity-50 disabled:cursor-not-allowed";

const labelBase =
  "block font-condensed uppercase tracking-[0.18em] text-white/70 text-xs mb-2";

const initialValues = {
  firstName: "",
  lastName: "",
  email: "",
  organizationName: "",
  personaCodes: [],
  sportCodes: [],
  otherPersonaText: "",
  otherSportText: "",
  marketingConsent: false,
  website: "",
};

export const WaitlistForm = () => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});
  const [attempted, setAttempted] = useState(false);

  // Options loading state
  const [options, setOptions] = useState(null);
  const [optionsStatus, setOptionsStatus] = useState("loading"); // loading | ready | error

  // Submission lifecycle: idle | submitting | success | error | rate_limited | validation
  const [submitState, setSubmitState] = useState("idle");

  // Attribution captured once on mount.
  const attributionRef = useRef(null);
  if (attributionRef.current === null) attributionRef.current = captureAttribution();

  const refs = {
    firstName: useRef(null),
    email: useRef(null),
    personas: useRef(null),
    otherPersonaText: useRef(null),
    sports: useRef(null),
    otherSportText: useRef(null),
    marketingConsent: useRef(null),
  };
  const successRef = useRef(null);
  const submitErrorRef = useRef(null);

  const loadOptions = useCallback(async () => {
    setOptionsStatus("loading");
    try {
      const data = await fetchWaitlistOptions();
      setOptions(data);
      setOptionsStatus("ready");
    } catch (_e) {
      setOptions(null);
      setOptionsStatus("error");
    }
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  useEffect(() => {
    if (submitState === "success" && successRef.current) {
      successRef.current.focus();
    }
  }, [submitState]);

  const ready = optionsStatus === "ready";
  const busy = submitState === "submitting";
  const otherPersonaSelected = values.personaCodes.includes(OTHER_CODE);
  const otherSportSelected = values.sportCodes.includes(OTHER_CODE);

  const setField = (name, value) => {
    setValues((prev) => {
      const next = { ...prev, [name]: value };
      if (attempted) setErrors(validateWaitlist(next));
      return next;
    });
    if (submitState !== "idle" && submitState !== "submitting") setSubmitState("idle");
  };

  const toggleCode = (key, code) => {
    setValues((prev) => {
      const list = prev[key];
      const next = {
        ...prev,
        [key]: list.includes(code)
          ? list.filter((c) => c !== code)
          : [...list, code],
      };
      if (attempted) setErrors(validateWaitlist(next));
      return next;
    });
    if (submitState !== "idle" && submitState !== "submitting") setSubmitState("idle");
  };

  const focusFirstInvalid = (errs) => {
    for (const field of FIELD_FOCUS_ORDER) {
      if (errs[field] && refs[field]?.current) {
        refs[field].current.focus();
        return;
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!ready || busy) return;
    setAttempted(true);
    const errs = validateWaitlist(values);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      focusFirstInvalid(errs);
      return;
    }
    setSubmitState("submitting");
    const result = await submitWaitlist({
      ...values,
      attribution: attributionRef.current,
    });
    if (result.ok) {
      setValues(initialValues);
      setAttempted(false);
      setErrors({});
      setSubmitState("success");
      return;
    }
    if (result.kind === "rate_limited") setSubmitState("rate_limited");
    else if (result.kind === "validation") setSubmitState("validation");
    else setSubmitState("error");
    // focus the error banner for screen-reader users
    setTimeout(() => submitErrorRef.current?.focus(), 0);
  };

  const errorList = useMemo(
    () => Object.entries(errors).filter(([, v]) => !!v),
    [errors]
  );

  // ---- Success state replaces the form ----
  if (submitState === "success") {
    return (
      <div
        data-testid={LANDING.successPanel}
        role="status"
        aria-live="polite"
        className="rounded-sm border border-[#C8A24C]/40 bg-[#C8A24C]/5 px-6 py-10 text-center"
      >
        <h3
          ref={successRef}
          tabIndex={-1}
          data-testid={LANDING.successHeading}
          className="font-condensed uppercase font-semibold text-white text-2xl sm:text-3xl tracking-tight focus:outline-none"
        >
          {COPY.successHeading}
        </h3>
        <p className="mt-3 text-white/70 text-base">{COPY.successMessage}</p>
      </div>
    );
  }

  const submitErrorMessage =
    submitState === "rate_limited"
      ? COPY.rateLimitMessage
      : submitState === "validation"
      ? COPY.validationMessage
      : submitState === "error"
      ? null
      : null;

  return (
    <form
      data-testid={LANDING.waitlistForm}
      onSubmit={handleSubmit}
      noValidate
      aria-busy={busy}
      className="space-y-7"
    >
      {/* Validation error summary (only after a submit attempt) */}
      {attempted && errorList.length > 0 && (
        <div
          data-testid={LANDING.errorSummary}
          role="alert"
          className="rounded-sm border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300"
        >
          Please correct the highlighted fields below.
        </div>
      )}

      {/* Submission error banner (recoverable / rate-limit / validation) */}
      {(submitState === "error" ||
        submitState === "rate_limited" ||
        submitState === "validation") && (
        <div
          ref={submitErrorRef}
          tabIndex={-1}
          data-testid={LANDING.submitError}
          role="alert"
          className="rounded-sm border border-red-500/40 bg-red-500/10 px-4 py-3 text-red-300 focus:outline-none"
        >
          {submitState === "error" ? (
            <>
              <p className="font-condensed uppercase tracking-[0.12em] text-sm text-red-200">
                {COPY.errorHeading}
              </p>
              <p className="mt-1 text-sm">{COPY.errorMessage}</p>
            </>
          ) : (
            <p className="text-sm">{submitErrorMessage}</p>
          )}
        </div>
      )}

      {/* Names */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="wl-first-name" className={labelBase}>
            First Name
          </label>
          <input
            id="wl-first-name"
            ref={refs.firstName}
            data-testid={LANDING.inputFirstName}
            type="text"
            value={values.firstName}
            onChange={(e) => setField("firstName", e.target.value)}
            autoComplete="given-name"
            disabled={busy}
            aria-required="true"
            aria-invalid={!!errors.firstName}
            aria-describedby={errors.firstName ? LANDING.fieldError("first-name") : undefined}
            className={inputBase}
          />
          <FieldError id={LANDING.fieldError("first-name")} message={errors.firstName} />
        </div>
        <div>
          <label htmlFor="wl-last-name" className={labelBase}>
            Last Name <span className="text-white/35">— Optional</span>
          </label>
          <input
            id="wl-last-name"
            data-testid={LANDING.inputLastName}
            type="text"
            value={values.lastName}
            onChange={(e) => setField("lastName", e.target.value)}
            autoComplete="family-name"
            disabled={busy}
            className={inputBase}
          />
        </div>
      </div>

      {/* Email */}
      <div>
        <label htmlFor="wl-email" className={labelBase}>
          Email Address
        </label>
        <input
          id="wl-email"
          ref={refs.email}
          data-testid={LANDING.inputEmail}
          type="email"
          value={values.email}
          onChange={(e) => setField("email", e.target.value)}
          autoComplete="email"
          disabled={busy}
          aria-required="true"
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? LANDING.fieldError("email") : undefined}
          className={inputBase}
        />
        <FieldError id={LANDING.fieldError("email")} message={errors.email} />
      </div>

      {/* Organization */}
      <div>
        <label htmlFor="wl-org" className={labelBase}>
          Organization Name <span className="text-white/35">— Optional</span>
        </label>
        <input
          id="wl-org"
          data-testid={LANDING.inputOrganization}
          type="text"
          value={values.organizationName}
          onChange={(e) => setField("organizationName", e.target.value)}
          autoComplete="organization"
          disabled={busy}
          className={inputBase}
        />
      </div>

      {/* Options loading / error states */}
      {optionsStatus === "loading" && (
        <div
          data-testid={LANDING.optionsLoading}
          role="status"
          aria-live="polite"
          className="text-sm text-white/55 py-2"
        >
          {COPY.optionsLoading}
        </div>
      )}

      {optionsStatus === "error" && (
        <div
          data-testid={LANDING.optionsError}
          role="alert"
          className="rounded-sm border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300"
        >
          <p>{COPY.optionsError}</p>
          <button
            type="button"
            data-testid={LANDING.optionsRetry}
            onClick={loadOptions}
            className="mt-2 font-condensed uppercase tracking-[0.16em] text-xs text-[#D9B872] underline underline-offset-2 hover:text-[#E7CA86]"
          >
            {COPY.tryAgain}
          </button>
        </div>
      )}

      {/* Personas + Sports (only when options are ready) */}
      {ready && options && (
        <>
          <fieldset disabled={busy}>
            <legend id="persona-legend" className={labelBase}>
              {COPY.personaLabel}
            </legend>
            <span ref={refs.personas} tabIndex={-1} data-testid={LANDING.personaGroup} />
            <MultiSelectOptionGroup
              legendId="persona-legend"
              options={options.personas}
              selected={values.personaCodes}
              onToggle={(code) => toggleCode("personaCodes", code)}
              testIdFor={LANDING.personaOption}
              invalid={!!errors.personas}
              errorId={LANDING.fieldError("personas")}
              disabled={busy}
            />
            <FieldError id={LANDING.fieldError("personas")} message={errors.personas} />

            {otherPersonaSelected && (
              <div className="mt-4">
                <label htmlFor="wl-persona-other" className={labelBase}>
                  {COPY.personaOtherLabel}
                </label>
                <input
                  id="wl-persona-other"
                  ref={refs.otherPersonaText}
                  data-testid={LANDING.personaOtherText}
                  type="text"
                  maxLength={OTHER_TEXT_MAX}
                  value={values.otherPersonaText}
                  onChange={(e) => setField("otherPersonaText", e.target.value)}
                  disabled={busy}
                  aria-required="true"
                  aria-invalid={!!errors.otherPersonaText}
                  aria-describedby={
                    errors.otherPersonaText ? LANDING.fieldError("other-persona") : undefined
                  }
                  className={inputBase}
                />
                <FieldError
                  id={LANDING.fieldError("other-persona")}
                  message={errors.otherPersonaText}
                />
              </div>
            )}
          </fieldset>

          <fieldset disabled={busy}>
            <legend id="sport-legend" className={labelBase}>
              {COPY.sportLabel}
            </legend>
            <span ref={refs.sports} tabIndex={-1} data-testid={LANDING.sportGroup} />
            <MultiSelectOptionGroup
              legendId="sport-legend"
              options={options.sports}
              selected={values.sportCodes}
              onToggle={(code) => toggleCode("sportCodes", code)}
              testIdFor={LANDING.sportOption}
              invalid={!!errors.sports}
              errorId={LANDING.fieldError("sports")}
              disabled={busy}
            />
            <FieldError id={LANDING.fieldError("sports")} message={errors.sports} />

            {otherSportSelected && (
              <div className="mt-4">
                <label htmlFor="wl-sport-other" className={labelBase}>
                  {COPY.sportOtherLabel}
                </label>
                <input
                  id="wl-sport-other"
                  ref={refs.otherSportText}
                  data-testid={LANDING.sportOtherText}
                  type="text"
                  maxLength={OTHER_TEXT_MAX}
                  value={values.otherSportText}
                  onChange={(e) => setField("otherSportText", e.target.value)}
                  disabled={busy}
                  aria-required="true"
                  aria-invalid={!!errors.otherSportText}
                  aria-describedby={
                    errors.otherSportText ? LANDING.fieldError("other-sport") : undefined
                  }
                  className={inputBase}
                />
                <FieldError
                  id={LANDING.fieldError("other-sport")}
                  message={errors.otherSportText}
                />
              </div>
            )}
          </fieldset>
        </>
      )}

      {/* Honeypot — visually hidden, kept in state, hidden from assistive tech */}
      <div className="corex-honeypot" aria-hidden="true">
        <label htmlFor="wl-website">Website</label>
        <input
          id="wl-website"
          data-testid={LANDING.honeypot}
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={values.website}
          onChange={(e) => setField("website", e.target.value)}
        />
      </div>

      {/* Consent */}
      <div>
        <label htmlFor="wl-consent" className="flex items-start gap-3 cursor-pointer">
          <input
            id="wl-consent"
            ref={refs.marketingConsent}
            data-testid={LANDING.consentCheckbox}
            type="checkbox"
            checked={values.marketingConsent}
            onChange={(e) => setField("marketingConsent", e.target.checked)}
            disabled={busy}
            aria-required="true"
            aria-invalid={!!errors.marketingConsent}
            aria-describedby={
              errors.marketingConsent ? LANDING.fieldError("consent") : undefined
            }
            className="mt-1 h-4 w-4 shrink-0 accent-[#C8A24C]"
          />
          <span className="text-sm text-white/70 leading-relaxed">
            {COPY.consent}
          </span>
        </label>
        <FieldError id={LANDING.fieldError("consent")} message={errors.marketingConsent} />
        <p className="mt-2 text-sm">
          <Link
            to="/privacy"
            data-testid={LANDING.privacyNoticeLink}
            className="text-[#D9B872] underline underline-offset-2 hover:text-[#E7CA86] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#D9B872] focus-visible:ring-offset-2 focus-visible:ring-offset-black rounded-sm"
          >
            {COPY.privacyNoticeLabel}
          </Link>
        </p>
      </div>

      {/* Submit */}
      <div>
        <button
          type="submit"
          data-testid={LANDING.submitButton}
          disabled={!ready || busy}
          aria-busy={busy}
          className="corex-btn-gold w-full font-condensed uppercase tracking-[0.18em] text-base font-semibold py-4 rounded-sm"
        >
          {busy ? COPY.submitting : COPY.submit}
        </button>
        <p className="mt-3 text-center text-xs text-white/45">
          {COPY.privacyReassurance}
        </p>
      </div>
    </form>
  );
};
